import React, { useState, useRef, useEffect } from 'react';
import { wordpressApi } from '../services/api';
import { Search, User, Building, Loader, X } from 'lucide-react';
import './CustomerSearch.css';

function CustomerSearch({ blogId, onSelect }) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const [error, setError] = useState(null);
  const searchTimeout = useRef(null);
  const wrapperRef = useRef(null);

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSearch = (value) => {
    setQuery(value);
    setError(null);

    if (searchTimeout.current) clearTimeout(searchTimeout.current);

    if (value.length < 2) {
      setResults([]);
      setShowDropdown(false);
      return;
    }

    searchTimeout.current = setTimeout(async () => {
      try {
        setLoading(true);
        const res = await wordpressApi.searchCustomers(blogId, value, { limit: 15 });
        setResults(res.data.customers || []);
        setShowDropdown(true);
      } catch (err) {
        // If WordPress isn't connected, fail silently - user can type manually
        setError('Search unavailable');
        setResults([]);
        setShowDropdown(false);
      } finally {
        setLoading(false);
      }
    }, 300);
  };

  const handleSelect = (customer) => {
    onSelect({
      name: customer.name,
      email: customer.email || '',
      phone: customer.phone || '',
      wp_type: customer.type,
      wp_id: customer.id
    });
    setQuery(customer.name);
    setShowDropdown(false);
  };

  return (
    <div className="customer-search" ref={wrapperRef}>
      <div className="customer-search-input">
        <Search size={16} />
        <input
          type="text"
          value={query}
          onChange={(e) => handleSearch(e.target.value)}
          onFocus={() => results.length > 0 && setShowDropdown(true)}
          placeholder="Search contacts & accounts..."
        />
        {loading && <Loader size={14} className="customer-search-spinner" />}
        {query && !loading && (
          <button className="customer-search-clear" onClick={() => { setQuery(''); setResults([]); setShowDropdown(false); }}>
            <X size={14} />
          </button>
        )}
      </div>

      {showDropdown && results.length > 0 && (
        <div className="customer-search-dropdown">
          {results.map((customer, idx) => (
            <button
              key={`${customer.type}-${customer.id}-${idx}`}
              className="customer-search-result"
              onClick={() => handleSelect(customer)}
            >
              <div className="customer-search-result-icon">
                {customer.type === 'contact' ? <User size={14} /> : <Building size={14} />}
              </div>
              <div className="customer-search-result-info">
                <span className="customer-search-result-name">{customer.name}</span>
                <span className="customer-search-result-detail">
                  {customer.email && <span>{customer.email}</span>}
                  {customer.phone && <span>{customer.phone}</span>}
                </span>
              </div>
              <span className="customer-search-result-type">{customer.type}</span>
            </button>
          ))}
        </div>
      )}

      {showDropdown && results.length === 0 && !loading && query.length >= 2 && (
        <div className="customer-search-dropdown">
          <div className="customer-search-empty">No results found</div>
        </div>
      )}

      {error && (
        <p className="customer-search-hint">WordPress search unavailable. Type customer info manually below.</p>
      )}
    </div>
  );
}

export default CustomerSearch;
